import imageCompression from 'browser-image-compression';
import { MAX_CONCURRENT, COMPRESSION_OPTIONS, isCompressible } from './constants';

export interface CompressionResult {
  path: string;
  originalSize: number;
  compressedSize: number;
  data: Blob;
  skipped: boolean;
}

class Semaphore {
  private queue: (() => void)[] = [];
  private running = 0;
  private max: number;

  constructor(max: number) {
    this.max = max;
  }

  async acquire(): Promise<void> {
    if (this.running < this.max) {
      this.running++;
      return;
    }
    return new Promise<void>((resolve) => {
      this.queue.push(() => {
        this.running++;
        resolve();
      });
    });
  }

  release(): void {
    this.running--;
    if (this.queue.length > 0) {
      const next = this.queue.shift()!;
      next();
    }
  }
}

export async function compressImages(
  entries: { path: string; data: Blob }[],
  targetSizeRatio: number,
  onProgress: (completed: number, total: number) => void,
): Promise<CompressionResult[]> {
  const semaphore = new Semaphore(MAX_CONCURRENT);
  const total = entries.length;
  let completed = 0;

  const tasks = entries.map(async (entry) => {
    await semaphore.acquire();
    try {
      if (!isCompressible(entry.path)) {
        completed++;
        onProgress(completed, total);
        return {
          path: entry.path,
          originalSize: entry.data.size,
          compressedSize: entry.data.size,
          data: entry.data,
          skipped: true,
        };
      }

      const file = new File([entry.data], entry.path.split('/').pop() || 'image', {
        type: getMimeType(entry.path),
      });

      const corrected = targetSizeRatio * 0.72;
      const targetMB = Math.max(0.005, (file.size / 1024 / 1024) * corrected);
      const compressed = await imageCompression(file, {
        ...COMPRESSION_OPTIONS,
        maxSizeMB: targetMB,
        initialQuality: Math.min(0.9, corrected),
      });

      completed++;
      onProgress(completed, total);

      return {
        path: entry.path,
        originalSize: entry.data.size,
        compressedSize: compressed.size,
        data: compressed as Blob,
        skipped: false,
      };
    } catch {
      completed++;
      onProgress(completed, total);
      return {
        path: entry.path,
        originalSize: entry.data.size,
        compressedSize: entry.data.size,
        data: entry.data,
        skipped: true,
      };
    } finally {
      semaphore.release();
    }
  });

  return Promise.all(tasks);
}

function getMimeType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase();
  const map: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    bmp: 'image/bmp',
    gif: 'image/gif',
    tiff: 'image/tiff',
    tif: 'image/tiff',
    avif: 'image/avif',
  };
  return map[ext ?? ''] ?? 'application/octet-stream';
}
